"use client";

import type { HistoryItemT } from "../constants/history-data";

type HistoryTimelineItemProps = {
  item: HistoryItemT;
  index: number;
  isActive: boolean;
  itemRef: (node: HTMLDivElement | null) => void;
};

/**
 * Individual timeline item component
 * Displays a year and associated content in a card format
 * Alternates left/right on desktop, stacked on mobile
 */
export default function HistoryTimelineItem({
  item,
  index,
  isActive,
  itemRef,
}: HistoryTimelineItemProps) {
  const isEven = index % 2 === 0;

  return (
    <div
      ref={itemRef}
      className="relative flex w-full items-start md:items-center"
    >
      {/* Mobile: Right side with content next to timeline */}
      {/* Desktop: Right side (even indices) or Left side (odd indices) */}
      <div
        className={`ml-[calc(1rem+2.25rem)] flex w-[calc(100%-3.25rem)] flex-col gap-3 md:ml-0 md:w-[calc(50%-2rem)] ${
          isEven ? "md:order-2 md:ml-auto md:pl-8" : "md:pr-8"
        }`}
      >
        {/* Content card */}
        <div
          className={`flex-1 rounded-lg bg-white p-4 transition-all md:p-6 ${
            isActive ? "shadow-md" : "opacity-40 shadow-md"
          }`}
        >
          <div
            className={`transition-colors ${
              isActive ? "text-foreground" : "text-muted-foreground"
            }`}
            dangerouslySetInnerHTML={{ __html: item.html }}
          />
        </div>
      </div>

      {/* Timeline marker - centered on desktop, left on mobile */}
      <div className="absolute top-0 left-[calc(1rem-0.75rem)] z-10 flex h-full shrink-0 items-center justify-center md:left-1/2 md:-translate-x-1/2">
        <div
          className={`relative flex items-center justify-center transition-all ${
            isActive ? "h-6 w-6 md:h-8 md:w-8" : "h-6 w-6 md:h-8 md:w-8"
          }`}
        >
          {/* Outer ring */}
          <div
            className={`absolute h-full w-full rounded-full border-6 border-[var(--color-wiest-blue)] transition-all md:border-9`}
          />
          {/* Inner solid circle - darker blue for active, white for inactive */}
          <div
            className={`h-3 w-3 rounded-full transition-all md:h-4 md:w-4 ${
              isActive ? "" : "bg-white"
            }`}
            style={
              isActive
                ? {
                    backgroundColor: "#1B4C85", // Darker blue than --color-wiest-blue (#1d3146)
                  }
                : undefined
            }
          />
        </div>
      </div>

      {/* Year label - above content on mobile (left), on opposite side of timeline on desktop */}
      <div
        className={`absolute -top-9 left-[calc(1rem+2.125rem)] z-10 flex shrink-0 items-center md:top-0 ${
          isEven
            ? "md:right-[calc(50%+2.5rem)] md:left-auto"
            : "md:right-auto md:left-[calc(50%+2.5rem)]"
        }`}
      >
        <span
          className={`inline-block rounded bg-gray-100 px-3 text-lg font-semibold transition-colors md:py-1.5 ${
            isActive
              ? "bg-[var(--color-wiest-blue)]/10 text-[var(--color-wiest-blue)]"
              : "text-muted-foreground opacity-60"
          }`}
        >
          {item.year}
        </span>
      </div>

      {/* Desktop: Empty space for opposite side */}
      <div
        className={`hidden w-[calc(50%-2rem)] md:block ${
          isEven ? "" : "order-2"
        }`}
      />
    </div>
  );
}
